import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { tap } from 'rxjs/operators';
import { Api } from 'src/app/shared/constants/api';
import { PermissionKey } from 'src/app/shared/constants/permissionCode';
import { IPermissionWithShow } from 'src/app/shared/interface';
import { DataService } from 'src/app/shared/services/data.service';
import { PermissionService } from 'src/app/shared/services/permissions.service';
import { UtilityService } from 'src/app/shared/services/utility.service';
import * as _ from 'underscore';
import { ILinerService, ILinerServiceCriteria } from '../liner-service.interface';
import { Subscription } from 'rxjs';
import { Message } from 'src/app/shared/constants/message';

@Component({
    selector: 'app-liner-service-list',
    templateUrl: './liner-service-list.component.html',
    styleUrls: ['./liner-service-list.component.less'],
    standalone: false
})
export class LinerServiceListComponent implements OnInit, AfterViewInit, OnDestroy {

  constructor(private logger:NGXLogger, private formBuilder:FormBuilder,
              private utilityService:UtilityService, private permissionService:PermissionService,
              private router:Router, private activatedRoute:ActivatedRoute,
              private service:DataService) { }

  pageSizeOptions=this.utilityService.getPageSizeOptions();

  viewPermission:boolean=false;
  addPermission:boolean=false;
  editPermission:boolean=false;
  reactivatePermission:boolean=false;
  deactivatePermission:boolean=false;
  linerServiceColumns=['lineName','consortiumName','polName','viaDestinationNames','destinationName','modeNames','status','action'];
  totalRecords:any;
  statusTypes:any;
  modes:any;
  linerServiceListForm:FormGroup;
  linerDataSource=new MatTableDataSource<ILinerService>();
  criteria:ILinerServiceCriteria={};
  @ViewChild(MatPaginator) paginator:MatPaginator;
  @ViewChild(MatSort) sort:MatSort;
  @ViewChild('table', { static: true }) table;
  idsToReactivate=[];
	subscription = new Subscription();

  ngOnInit(): void {
    this.checkAllPermission();
    this.initializeForm();
    this.getStatusTypes();
    this.getModes();
    this.setLineNameValueChangeEvent();
    this.setConsortiumNameValueChangeEvent();
    this.setDestinationNameValueChangeEvent();
    this.setPolValueChangeEvent();
    this.setViaDestinationNameValueChangeEvent();
    this.setStatusValueChangeEvent();
  }

  initializeForm(){
    this.linerServiceListForm=this.formBuilder.group({
      id:[],
      consortiumName:[],
      lineName:[],
      polName:[],
      destinationName:[],
      viaDestinationName:[],
      mode:[],
      status:[]
    })
  }

  ngAfterViewInit() {
    this.linerDataSource.paginator = this.paginator;
    this.linerDataSource.sort = this.sort;
    this.paginator?.page?.pipe(tap(() => this.getLinerServiceList())).subscribe();
    this.getLinerServiceList();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  sortData(event){
    this.criteria.sort = [event.active, event.direction];
    this.getLinerServiceList(true);
  }

  setConsortiumNameValueChangeEvent(){
    this.subscription = this.consortiumName.valueChanges.subscribe(consortiumValue => {
       this.criteria.consortiumName = consortiumValue;
       this.getLinerServiceList(true);
    });
  }

  setViaDestinationNameValueChangeEvent(){
    const sub = this.viaDestinationName.valueChanges.subscribe(viaDestinationValue => {
       this.criteria.viaDestinationName = viaDestinationValue;
       this.getLinerServiceList();
    });
    this.subscription.add(sub);
  }

  setLineNameValueChangeEvent(){
    const sub = this.lineName.valueChanges.subscribe(lineValue => {
       this.criteria.lineName=lineValue;
       this.getLinerServiceList();
    });
    this.subscription.add(sub);
  }

  setPolValueChangeEvent(){
    const sub = this.pol.valueChanges.subscribe(polValue => {
       this.criteria.polName = polValue;
       this.getLinerServiceList();
    });
    this.subscription.add(sub);
  }

  setDestinationNameValueChangeEvent(){
    const sub = this.destinationName.valueChanges.subscribe(destinationNameValue => {
       this.criteria.destinationName = destinationNameValue;
       this.getLinerServiceList();
    });
    this.subscription.add(sub);
  }

  setModeValueChangeEvent(){
    const sub = this.mode.valueChanges.subscribe(modeValue => {
       this.criteria.mode = modeValue;
       this.getLinerServiceList();
    });
    this.subscription.add(sub);
  }

  setStatusValueChangeEvent(){
    const sub = this.status.valueChanges.subscribe(statusValue => {
       this.criteria.status = statusValue;
       this.getLinerServiceList();
    });
    this.subscription.add(sub);
  }

  openLinerForm(id?:number){
    if(id){
      this.editPermission ? this.router.navigate(['edit/'+id],{relativeTo:this.activatedRoute}) :
        this.utilityService.notifier(Message.EDIT_PERMISSION_WARNING,"warning");
      } else {
      this.addPermission ? this.router.navigate(['add'],{relativeTo:this.activatedRoute}) :
      this.utilityService.notifier(Message.ADD_PERMISSION_WARNING,"warning");
    }
  }

  activateAllLinerServices(){
    if(this.reactivatePermission){ 
      if(this.idsToReactivate.length>0){
        this.service.post(Api.LINER_SERVICE_REACTIVATE_GLOBALLY,this.idsToReactivate).subscribe(res => {
            this.utilityService.notifier('Liner Services Reactivated Successfully','success');
            this.getLinerServiceList();
        });
      }
    } else 
      this.utilityService.notifier(Message.ACTIVATE_PERMISSION_WARNING,"warning");
  }

  deactivateChanged(id:number,event:any){
    if(event.checked){
      this.idsToReactivate.push(id);
    } else {
      let index=this.idsToReactivate.indexOf(id);
      this.idsToReactivate.splice(index,1);
    }
    this.logger.info('ids to reactivate',this.idsToReactivate);
  }

  getStatusTypes(){
    this.service.get(Api.STATUS_TYPES_LIST).subscribe(res => {
      if(res){
        this.statusTypes=res;
      }
    })
  }

  getLinerServiceList(resetPageIndex: boolean =false){
    if (resetPageIndex) {
      this.paginator.pageIndex = 0;
    }
    this.criteria.page=this.paginator.pageIndex;
    this.criteria.size=this.paginator.pageSize;
    this.criteria.sort= this.criteria.sort? this.criteria.sort : ['createdDate','desc'];
    if(this.viewPermission)
    this.service.get(Api.LINER_SERVICE,this.criteria).subscribe(res => {
       if(res){
        if(res.contents.length == 0){
          this.utilityService.notifier('No records found','info');
        }
         this.logger.info('Liner Service List',res);
         this.linerDataSource=res.contents;
         this.totalRecords=res.totalRecords;
         if (this.paginator.pageIndex >= Math.ceil(this.totalRecords / this.paginator.pageSize)) {
          this.paginator.pageIndex = 0;
        }
       }
    });
    else
      this.utilityService.notifier(Message.VIEW_PERMISSION_WARNING,"warning");
  }

  getModes(){
    this.service.get(Api.MODE_LIST).subscribe(res => {
      if(res){
        this.modes=res;
      }
    });
  }

  public checkAllPermission(){
    const permissionCodes=[PermissionKey.LINER_SERVICE_MODULE_ADD_PERMISSION,PermissionKey.LINER_SERVICE_MODULE_VIEW_PERMISSION,
      PermissionKey.LINER_SERVICE_MODULE_REACTIVATE_PERMISSION,PermissionKey.LINER_SERVICE_MODULE_EDIT_PERMISSION,
      PermissionKey.LINER_SERVICE_MODULE_DEACTIVATE_PERMISSION];
    const permissionsWithShowValue:IPermissionWithShow[]=this.permissionService.checkPermissions(permissionCodes,PermissionKey.LINER_SERVICE_MODULE);
    this.addPermission=_.findWhere(permissionsWithShowValue,{permissionCode:PermissionKey.LINER_SERVICE_MODULE_ADD_PERMISSION})?.show?true:false;
    this.viewPermission=_.findWhere(permissionsWithShowValue,{permissionCode:PermissionKey.LINER_SERVICE_MODULE_VIEW_PERMISSION})?.show?true:false;
    this.editPermission=_.findWhere(permissionsWithShowValue,{permissionCode:PermissionKey.LINER_SERVICE_MODULE_EDIT_PERMISSION})?.show?true:false;
    this.reactivatePermission=_.findWhere(permissionsWithShowValue,{permissionCode:PermissionKey.LINER_SERVICE_MODULE_REACTIVATE_PERMISSION})?.show?true:false;
    this.deactivatePermission=_.findWhere(permissionsWithShowValue,{permissionCode:PermissionKey.LINER_SERVICE_MODULE_DEACTIVATE_PERMISSION})?.show?true:false;
  }

  get id(){return this.linerServiceListForm.get('id'); }
  get consortiumName(){return this.linerServiceListForm.get('consortiumName'); }
  get lineName(){return this.linerServiceListForm.get('lineName'); }
  get pol(){return this.linerServiceListForm.get('polName'); }
  get destinationName(){return this.linerServiceListForm.get('destinationName'); }
  get viaDestinationName(){return this.linerServiceListForm.get('viaDestinationName'); }
  get mode(){return this.linerServiceListForm.get('mode'); }
  get status(){return this.linerServiceListForm.get('status'); }
}
